import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Preferences } from "@capacitor/preferences";
import { Seo } from "@/components/Seo";
import { Wordmark } from "@/components/editorial";

const PREF_KEY_ACK = "bc.disclaimer_acknowledged_at";
const PREF_KEY_VER = "bc.disclaimer_version";

/**
 * Full legal text page — accessible from the profile / settings tab.
 * Shows the disclaimer version and the date the user acknowledged it.
 */
export function LegalPage() {
  const navigate = useNavigate();
  const [acknowledgedAt, setAcknowledgedAt] = useState<string | null>(null);
  const [version, setVersion] = useState<number | null>(null);

  useEffect(() => {
    async function load() {
      const { value: ack } = await Preferences.get({ key: PREF_KEY_ACK });
      const { value: ver } = await Preferences.get({ key: PREF_KEY_VER });
      setAcknowledgedAt(ack);
      setVersion(ver ? Number.parseInt(ver, 10) : null);
    }
    void load();
  }, []);

  const formattedDate = acknowledgedAt
    ? new Intl.DateTimeFormat("fr-FR", {
        dateStyle: "long",
        timeStyle: "short",
      }).format(new Date(acknowledgedAt))
    : null;

  return (
    <div className="min-h-full bg-background">
      <Seo
        title="Mentions légales"
        description="Mentions légales de Batch Cooking : clause de non-responsabilité concernant les recettes générées par IA, allergènes, conservation des aliments."
        canonicalPath="/legal"
      />
      {/* Header */}
      <header className="flex items-center justify-between px-5 pt-6">
        <button
          onClick={() => navigate(-1)}
          aria-label="Retour"
          className="flex h-9 w-9 items-center justify-center text-foreground"
        >
          <ArrowLeft className="size-5" />
        </button>
        <Wordmark size="sm" />
      </header>

      {/* Content */}
      <div className="mx-auto max-w-lg px-6 pb-16 pt-6">
        <h1 className="font-serif text-[26px] font-normal leading-[1.2] text-foreground">
          Mentions légales
        </h1>

        {/* Acknowledgement status */}
        {formattedDate && (
          <div className="mt-3 rounded border border-hair bg-paper px-4 py-3 text-[12px] text-ink-60">
            <p>
              Version acceptée : <strong>v{version ?? "?"}</strong>
            </p>
            <p>
              Acceptée le : <strong>{formattedDate}</strong>
            </p>
          </div>
        )}

        {!formattedDate && (
          <div className="mt-3 rounded border border-hair bg-paper px-4 py-3 text-[12px] text-ink-40 italic">
            Vous n'avez pas encore accepté la clause de non-responsabilité.
          </div>
        )}

        {/* Legal text */}
        <div className="mt-6 space-y-5 text-[14px] leading-relaxed text-ink-80">
          <section>
            <h2 className="mb-1.5 font-semibold text-foreground">
              1. Génération par intelligence artificielle
            </h2>
            <p>
              Les recettes, conseils de conservation, estimations de durée et
              autres contenus présentés sur Batch Cooking sont générés par
              intelligence artificielle (IA). Bien que nous nous efforçons de
              fournir des informations pertinentes et de qualité, ces contenus
              peuvent contenir des erreurs, des omissions ou des imprécisions.
            </p>
          </section>

          <section>
            <h2 className="mb-1.5 font-semibold text-foreground">
              2. Estimations de durée de conservation
            </h2>
            <p>
              Les durées de conservation indiquées sont des{" "}
              <strong>estimations indicatives</strong>. Elles ne constituent pas
              une garantie de fraîcheur ou de sécurité alimentaire. La durée de
              conservation réelle dépend de nombreux facteurs : qualité des
              produits frais, conditions de transport, température du
              réfrigérateur, respect de la chaîne du froid, etc.
            </p>
            <p className="mt-2">
              Il vous incombe de vérifier l'état des aliments avant
              consommation. En cas de doute sur la fraîcheur d'un aliment,
              jetez-le.
            </p>
          </section>

          <section>
            <h2 className="mb-1.5 font-semibold text-foreground">
              3. Allergies, intolérances et restrictions alimentaires
            </h2>
            <p>
              Batch Cooking vous permet d'indiquer des allergies et restrictions
              alimentaires dans votre profil. Cependant :
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                La base de données et les modèles d'IA peuvent ne pas avoir
                connaissance de tous les allergènes possibles ou de leurs
                sources cachées.
              </li>
              <li>
                Les recettes générées peuvent contenir des traces d'allergènes
                non déclarés.
              </li>
              <li>
                Les substitutions d'ingrédients suggérées peuvent ne pas
                convenir à tous les types de restrictions.
              </li>
            </ul>
            <p className="mt-2">
              <strong>
                Il est de votre entière responsabilité de vérifier la
                composition de chaque recette et de vous assurer qu'elle est
                adaptée à vos besoins alimentaires.
              </strong>{" "}
              En cas de doute, consultez un professionnel de santé.
            </p>
          </section>

          <section>
            <h2 className="mb-1.5 font-semibold text-foreground">
              4. Absence de conseil médical ou nutritionnel
            </h2>
            <p>
              Les informations fournies par Batch Cooking (apports caloriques,
              répartitions nutritionnelles, suggestions diététiques) le sont à
              titre indicatif uniquement. Elles ne sauraient se substituer :
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>À un avis médical professionnel</li>
              <li>À un suivi nutritionnel personnalisé</li>
              <li>
                À un régime thérapeutique prescrit par un médecin ou un
                diététicien
              </li>
            </ul>
            <p className="mt-2">
              Batch Cooking n'est pas un service de télémédecine et ne fournit
              pas de conseil médical.
            </p>
          </section>

          <section>
            <h2 className="mb-1.5 font-semibold text-foreground">
              5. Limitation de responsabilité
            </h2>
            <p>
              Dans toute la mesure permise par la loi applicable, Batch Cooking,
              ses développeurs, ses opérateurs et ses ayants droit déclinent
              toute responsabilité pour :
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                Tout dommage direct ou indirect résultant de l'utilisation des
                contenus générés
              </li>
              <li>
                Tout problème de santé, intoxication alimentaire, réaction
                allergique ou autre conséquence liée à l'exécution des recettes
                ou à la consommation des plats préparés
              </li>
              <li>
                Toute perte ou détérioration d'aliments due à une conservation
                inadéquate
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-1.5 font-semibold text-foreground">
              6. Acceptation
            </h2>
            <p>
              En utilisant Batch Cooking, vous reconnaissez avoir lu, compris et
              accepté les termes de cette clause de non-responsabilité. Si vous
              n'acceptez pas ces termes, veuillez cesser d'utiliser
              l'application immédiatement.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-10 border-t border-hair pt-5 text-center text-[11px] text-ink-30">
          <p>Version 1 — 17 mai 2026</p>
          {formattedDate && <p className="mt-1">Acceptée le {formattedDate}</p>}
        </div>
      </div>
    </div>
  );
}
